// Peak / Off-Peak pricing for Personal Training packages
//
// Off-peak: Mon–Fri 9:00 to 16:00 (i.e. startTime >= 09:00 AND startTime < 16:00 on a weekday)
// Peak:    everything else (weekends, or weekdays before 9am / after 4pm)
//
// Peak premium adds $10 per session.

export const PT_PEAK_PREMIUM = 10;
export const PT_FEMALE_TRAINER_PREMIUM = 20;
// Exclusive online-booking discount applied per session (off the gross per-session rate)
export const PT_ONLINE_BOOKING_DISCOUNT_PER_SESSION = 10;

const parseHour = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const parts = timeStr.split(':');
    if (parts.length < 2) return null;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return null;
    return h + (m / 60);
};

const parseDate = (dateStr) => {
    if (!dateStr) return null;
    // Accept Date object or string (e.g. "2026-05-04" or "04-05-2026")
    if (dateStr instanceof Date) return dateStr;
    // Try YYYY-MM-DD first
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (isoMatch) {
        return new Date(parseInt(isoMatch[1], 10), parseInt(isoMatch[2], 10) - 1, parseInt(isoMatch[3], 10));
    }
    // Try DD-MM-YYYY (FormatStringDateToJavaDate format used elsewhere)
    const dmyMatch = /^(\d{2})-(\d{2})-(\d{4})$/.exec(dateStr);
    if (dmyMatch) {
        return new Date(parseInt(dmyMatch[3], 10), parseInt(dmyMatch[2], 10) - 1, parseInt(dmyMatch[1], 10));
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d;
    return null;
};

/**
 * Determine whether the given service date + start time falls in peak window.
 * If either is missing, defaults to off-peak (false).
 */
export const isPeakSlot = (serviceDate, startTime) => {
    const date = parseDate(serviceDate);
    const startHour = parseHour(startTime);
    if (!date || startHour == null) return false;
    const dow = date.getDay(); // 0 = Sun, 6 = Sat
    const isWeekend = dow === 0 || dow === 6;
    if (isWeekend) return true;
    // Weekday: peak if before 9am or 4pm or later
    if (startHour < 9 || startHour >= 16) return true;
    return false;
};

/**
 * Pull a criteria value from the requestCriteria array (each entry is keyed by
 * a question code).
 */
export const getCriteriaValue = (requestCriteria, questionCode) => {
    if (!Array.isArray(requestCriteria)) return null;
    for (const item of requestCriteria) {
        if (item && item[questionCode] !== undefined && item[questionCode] !== null) {
            return item[questionCode];
        }
    }
    return null;
};

export const isFemaleTrainerPreference = (requestCriteria) => {
    const v = getCriteriaValue(requestCriteria, 4);
    return typeof v === 'string' && v.toLowerCase() === 'female';
};

/**
 * Compute price for a personal-training package given the (optionally) chosen
 * service date / start time and the request criteria (for trainer-gender
 * surcharge).
 *
 * @param {Object} pkg - one of personalTrainerPackages[tier].packages
 * @param {string|Date} serviceDate
 * @param {string} startTime - "HH:mm"
 * @param {Array}  [requestCriteria] - optional, used to detect female-trainer surcharge
 * @returns {{ perSessionPrice: number, totalCost: number, isPeak: boolean, peakPremium: number, isFemaleTrainer: boolean, femalePremium: number, basePerSessionPrice: number, totalServices: number }}
 */
/**
 * Compute price for a personal-training package.
 *
 * @param {Object} pkg
 * @param {string|Date} serviceDate
 * @param {string} startTime
 * @param {Array}  [requestCriteria]
 * @param {Object} [opts]
 * @param {boolean} [opts.applyOnlineDiscount=false] - apply the exclusive online-booking discount per session
 */
export const computePackagePrice = (pkg, serviceDate, startTime, requestCriteria, opts = {}) => {
    const { applyOnlineDiscount = false } = opts;

    const basePerSessionPrice = pkg?.perSessionPrice || 0;
    const totalServices = pkg?.totalServices || 0;
    const peak = isPeakSlot(serviceDate, startTime);
    const female = isFemaleTrainerPreference(requestCriteria);

    // Gross per-session (before any online-booking discount)
    const grossPerSessionPrice =
        basePerSessionPrice +
        (peak ? PT_PEAK_PREMIUM : 0) +
        (female ? PT_FEMALE_TRAINER_PREMIUM : 0);

    // Online-booking discount only when explicitly enabled
    const onlineDiscountPerSession = applyOnlineDiscount
        ? Math.min(grossPerSessionPrice, PT_ONLINE_BOOKING_DISCOUNT_PER_SESSION)
        : 0;
    const perSessionPrice = grossPerSessionPrice - onlineDiscountPerSession;

    const originalTotalCost = grossPerSessionPrice * totalServices;
    const totalCost = perSessionPrice * totalServices;
    const totalSavings = originalTotalCost - totalCost;

    return {
        perSessionPrice,
        grossPerSessionPrice,
        totalCost,
        originalTotalCost,
        isPeak: peak,
        peakPremium: PT_PEAK_PREMIUM,
        isFemaleTrainer: female,
        femalePremium: PT_FEMALE_TRAINER_PREMIUM,
        onlineDiscountPerSession,
        totalSavings,
        basePerSessionPrice,
        totalServices
    };
};
